import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import _ from 'lodash';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useTranslation from 'hooks/use-translation';
import useUser from 'src/hooks/use-user';
import { useModals, ModalNames } from 'components/modals';

import BogoMenuCard from 'shared/components/specials/bogo-menu-card';
import { DesktopOnly, MobileOnly } from 'shared/components';
import { sortSpecialCards } from 'shared/helpers/specials';
import { Typography } from 'src/typography';
import { BreadCrumbs } from 'src/components/bread-crumbs';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import { SpecialOfferCard } from 'src/components/special-offer-card';

function OfferMenuSection({ UI, dispensary, specials, pageTitle, pageDescription = '', showDescription = true }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { isDutchieMain } = UI;
  const { openModal } = useModals();
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const flags = useFlags();
  const specialsRedesignEnabled = flags['ecomm.specials-redesign.rollout'] ?? false;
  const specialsToRender = specials;

  const hideSalesLanguage = useHideSalesLanguage();
  const resolvedPageTitle =
    pageTitle ||
    (hideSalesLanguage ? t(`common.todaysOffers`, `Today's Offers`) : t(`common.todaysSpecials`, `Today's Specials`));

  const pageDescriptionText = hideSalesLanguage ? `Shop today's offers!` : `Shop today's sales and special offers!`;
  const resolvedPageDescription = pageDescription || t(`specials.clickASpecial`, pageDescriptionText);

  const sortedByExpiration = sortSpecialCards(specialsToRender);
  const numPlaceholdersToRender = !(sortedByExpiration.length % 3) ? 0 : 3 - (sortedByExpiration.length % 3);

  const getSpecialUrl = (id, specialType) => {
    const specialTypeSubRoute = specialType === `sale` ? specialType : `offer`;
    if (!dispensary) {
      return null;
    }

    return `/${UI.dispensaryRootUrl}/${dispensary.cName}/specials/${specialTypeSubRoute}/${id}`;
  };

  // Check if there are any restricted specials (members-only offers)
  const hasRestrictedSpecials = _.some(specialsToRender, 'isRestricted');

  const handleLoginClick = () => {
    openModal(ModalNames.login);
  };

  function renderGridPlaceholders() {
    return _.map(Array(numPlaceholdersToRender), (__, index) => (
      <NonVisiblePlaceholder key={`offers_grid_placeholder_${index}`} aria-hidden className='bogo-card-container' />
    ));
  }

  if (specialsToRender.length === 0) {
    return null;
  }

  return (
    <Section>
      {isDutchieMain && (
        <BreadCrumbsContainer isMobile={isMobile}>
          <BreadCrumbs />
        </BreadCrumbsContainer>
      )}
      <Typography.Heading size='medium' tag='h1'>
        {resolvedPageTitle}
      </Typography.Heading>
      {showDescription && (
        <>
          <DesktopOnly display='block'>
            <Label>{resolvedPageDescription}</Label>
          </DesktopOnly>
          <MobileOnly display='block'>
            <Label>{resolvedPageDescription}</Label>
          </MobileOnly>
        </>
      )}

      {!isLoggedIn && hasRestrictedSpecials && (
        <LoginPrompt>
          <LoginIcon>
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z'
                fill='currentColor'
              />
              <path
                d='M10 12C4.47715 12 0 14.4772 0 17.5C0 18.8807 1.11929 20 2.5 20H17.5C18.8807 20 20 18.8807 20 17.5C20 14.4772 15.5228 12 10 12Z'
                fill='currentColor'
              />
            </svg>
          </LoginIcon>
          <LoginText>Sign in to see exclusive member offers</LoginText>
          <LoginButton onClick={handleLoginClick} data-testid='specials-login-button'>
            Sign In
          </LoginButton>
        </LoginPrompt>
      )}

      {specialsRedesignEnabled ? (
        <SpecialsGridContainer>
          {_.map(sortedByExpiration, (special) => {
            const specialUrl = getSpecialUrl(special._id, special.specialType);
            return specialUrl ? (
              <SpecialOfferCard key={special._id} special={special} href={specialUrl} inGridLayout />
            ) : null;
          })}
        </SpecialsGridContainer>
      ) : (
        <OuterContainer>
          {_.map(sortedByExpiration, (special, i) => {
            const specialUrl = getSpecialUrl(special._id, special.specialType);
            return (
              <Link href={specialUrl} key={special._id}>
                <BogoMenuCard
                  displayName={special.menuDisplayName || special.name}
                  displayImage={special.menuDisplayImage}
                  className={`BogoCard_${i}`}
                />
              </Link>
            );
          })}
          {/* the last row can calculate the widths of the cards incorrectly
              so this forces some invisible elements in to keep widths consistent */}
          {renderGridPlaceholders()}
        </OuterContainer>
      )}
    </Section>
  );
}

const Label = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[35]};
  line-height: 30px;
  margin: 0 0 14px;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding-left: 25px;
    margin: 0;
  }
`;

// TODO: Delete once 'ecomm.specials-redesign.rollout' hits 100% — superseded by SpecialsGridContainer.
const OuterContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 15px 0 16px 0;
  width: 100%;
  flex-wrap: wrap;
  padding: 0 20px 15px;
  display: inline-flex;
  overflow-x: scroll;
  overflow-y: hidden;
  margin-bottom: -15px; // offset the bottom padding

  > a {
    display: contents;
  }

  &&& .bogo-card-container {
    max-width: 100%;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 600px) {
    justify-content: space-between;

    &&& .bogo-card-container {
      margin-right: 0;
      margin-left: 0;
      max-width: calc(50% - 12px);
    }

    &:nth-child(odd) {
      margin-left: 0;
    }
  }

  @media (min-width: 960px) {
    padding-left: 0;
  }

  @media (min-width: 1280px) {
    flex-wrap: wrap;
    margin-bottom: 0;
    padding-left: 0;

    &&& .bogo-card-container {
      margin-right: 25px;

      &:nth-child(3n + 3) {
        margin-right: -20px;
      }
    }
  }
`;

const SpecialsGridContainer = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
  margin: 15px 0 16px;
  padding: 0 20px 15px;
  width: 100%;

  > a {
    min-width: 0;
  }

  @media (min-width: 600px) {
    gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 960px) {
    padding: 0;
  }
`;

const NonVisiblePlaceholder = styled.div`
  visibility: hidden;
  width: 383px;
`;

const Section = styled.div`
  margin-top: 44px;
  @media (min-width: 960px) and (max-width: 1280px) {
    padding-left: 20px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-top: unset;
  }
`;

const BreadCrumbsContainer = styled.div`
  margin: ${({ isMobile }) => (isMobile ? `25px 0 -25px` : `-18px 0 24px -25px`)};
`;

const LoginPrompt = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f0f9f5 0%, #e3f3eb 100%);
  border: 1px solid #c7e5d6;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 16px 0 24px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: 16px 20px 24px;
    flex-wrap: wrap;
    justify-content: center;
    text-align: center;
  }
`;

const LoginIcon = styled.div`
  color: #00624a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LoginText = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #2e3438;
  line-height: 1.4;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    flex-basis: 100%;
    order: -1;
    margin-bottom: 8px;
  }
`;

const LoginButton = styled.button`
  background: #00624a;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #004d3a;
  }

  &:active {
    background: #003d2e;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

export default OfferMenuSection;
